import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Chip,
  Button,
} from "@mui/material";
import { db, auth } from "../../firebase-config";
import { collection, getDocs, query, where, or, doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";

const QuoteList = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const fetchUserCompanyId = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setCompanyId(userSnap.data().companyId || null);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserCompanyId();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || companyId === undefined) return; // Wait for companyId to load

    const fetchQuotes = async () => {
      try {
        const quotesRef = collection(db, "quotes");
        let q;

        if (companyId) {
          // Firestore OR query: Get quotes where userId matches OR companyId matches
          q = query(
            quotesRef,
            or(
              where("userId", "==", currentUser.uid),
              where("companyId", "==", companyId)
            )
          );
        } else {
          // Fetch only user-specific quotes if no companyId
          q = query(quotesRef, where("userId", "==", currentUser.uid));
        }

        const snapshot = await getDocs(q);
        const quoteList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setQuotes(quoteList);
      } catch (err) {
        console.error("Error fetching quotes:", err);
        setError("Failed to load quotes.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [currentUser, companyId]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Your Quotes
      </Typography>

      {quotes.length === 0 ? (
        <Typography>No quotes found.</Typography>
      ) : (
        <Box>
          {quotes.map((quote) => (
            <Card key={quote.id} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6">
                  Quote ID: {quote.id}{" "}
                  <Chip
                    label={quote.status.toUpperCase()}
                    color={
                      quote.status === "pending"
                        ? "warning"
                        : quote.status === "approved"
                        ? "success"
                        : "error"
                    }
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Requested on: {new Date(quote.createdAt.toDate()).toLocaleString()}
                </Typography>

                {/* Button to navigate to quote details */}
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={`/quote/${quote.id}`}
                  sx={{ mt: 2 }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default QuoteList;
